/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/no-unsafe-return */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import NavBarWrapper from './NavBarWrapper';
import NavItems from './NavItems';
import { TrackingServiceContext, useInPageDispatch, useInPageNavContext } from '../../context';
import { INPAGENAV_ACTIONS } from '../../reducers/inPageNavReducer';
import { ActiveLinks } from '../../../types';
import OverViewLabel from './OverViewLabel';
import { useSubLinks } from '../../hooks/use-sub-links';
import useEventListener from '../../hooks/use-event-listener';
import { handleKeyDown, trapFocus } from '../../utils/a11y';
import { getElementsByClass, SECOND_LEVEL_LINK_INDEX } from '../../utils/utils';

interface IProps {
  visible: boolean;
  toggle: () => void;
}

const SecondLevelNavDesktop = (props: IProps) => {
  const dispatch = useInPageDispatch();
  const { visible, toggle } = props;
  useContext(TrackingServiceContext);

  const {
    sameLevelInpageNavigation,
    topLevelActiveLink,
    sameLevelActiveLink,
    secondLevelActiveLink,
    priorityNavigation,
    priorityNavActive,
    subNavActive,
  } = useInPageNavContext();
  const { subLinks } = useSubLinks(
    visible,
    sameLevelInpageNavigation,
    topLevelActiveLink,
    sameLevelActiveLink,
    secondLevelActiveLink,
    subNavActive,
  );
  const [refNavUpdated, setRefNavUpdated] = useState<React.RefObject<HTMLElement>>();

  const currentTopLevelLink = useMemo(() => {
    if (!topLevelActiveLink) return null;

    const priorityNavLength = priorityNavigation?.length || 0;
    const topLinkIndex = topLevelActiveLink.index || 0;
    const adjustedIndex = (topLinkIndex % priorityNavLength) - 1;
    const arrIndex =
      priorityNavActive && priorityNavigation?.length ? adjustedIndex : topLevelActiveLink.index;

    const links = Array.from(
      getElementsByClass('top-level-link') as HTMLCollectionOf<HTMLAnchorElement>,
    ).find((_, index) => index === arrIndex);

    return links || null;
  }, [topLevelActiveLink, priorityNavActive, priorityNavigation?.length]);

  // apply active class to second level links and second level labels
  useEffect(() => {
    if (!secondLevelActiveLink) return undefined;
    const currentPath = typeof window !== 'undefined' ? window?.location?.pathname : '';

    const topLinksIndex = secondLevelActiveLink?.index;
    const secondNavLinks = document.querySelectorAll<HTMLAnchorElement>(
      `.${SECOND_LEVEL_LINK_INDEX}-${topLinksIndex}`,
    );

    const activatedElements: HTMLAnchorElement[] = [];
    secondNavLinks.forEach((element) => {
      const elemUrl = element.getAttribute('href') || element.dataset.href;
      if (elemUrl === currentPath) {
        element.classList.add('active-link');
        activatedElements.push(element);
      }
    });

    return () => {
      activatedElements.forEach((element) => element.classList.remove('active-link'));
    };
  }, [subLinks, secondLevelActiveLink]);

  const handleSelectedItem = useCallback(
    ({ id, index, text, href, target }: ActiveLinks) => {
      dispatch?.({
        type: INPAGENAV_ACTIONS.SUB_LEVEL_ACTIVE_LINK,
        payload: {
          secondLevelActiveLink: { id, index, text, href, target, overviewLinkSelected: false },
        },
      });
    },
    [dispatch, topLevelActiveLink, sameLevelActiveLink],
  );

  const handleListRef = useCallback(
    (ref?: React.RefObject<HTMLElement>) => {
      if (ref?.current?.classList.contains('inpage-nav-menu')) {
        setRefNavUpdated(ref);
      }
    },
    [setRefNavUpdated],
  );

  useEffect(() => {
    if (subNavActive && refNavUpdated) {
      // get the first anchor element in submenu
      setTimeout(() => {
        trapFocus(refNavUpdated?.current, currentTopLevelLink as HTMLElement);
      }, 100);
    }
  }, [refNavUpdated, subNavActive, currentTopLevelLink]);
  // a11y keyboard navigation
  useEventListener('keydown', (e: KeyboardEvent | Event) => {
    if (e instanceof KeyboardEvent) {
      if (e.key === 'Escape' && visible) {
        toggle();
        currentTopLevelLink?.focus();
      }
      if (refNavUpdated) handleKeyDown(e, refNavUpdated.current);
    }
  });

  return (
    subLinks?.length > 0 && (
      <NavBarWrapper visible={visible} handleListRef={handleListRef} isSecondaryLevel>
        <OverViewLabel isSecondaryLevel />
        {subLinks?.map((item, i) => {
          const { fields } = item;
          return (
            <NavItems
              // eslint-disable-next-line react/no-array-index-key
              key={`nav-item-${i}`}
              id={item.id}
              fields={fields}
              index={i}
              hasParentElement={false}
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              handleSelectedItem={handleSelectedItem}
              fieldsLength={subLinks?.length}
              isSecondaryLevel
            />
          );
        })}
      </NavBarWrapper>
    )
  );
};

export default SecondLevelNavDesktop;
